import { Fragment, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CircleCheck, Lock, RotateCcw } from 'lucide-react';
import dayjs from 'dayjs';

import { Bubble, BubbleContent } from '@/components/ui/bubble';
import { cn } from '@/lib/utils';
import type { SupportMessage, TaskStatus, TaskType } from '../../../common/models/support.model';
import { TASK_STATUS_META, TASK_TYPE_META } from '../support-meta';
import { AttachmentView } from './AttachmentView';

const GROUP_WINDOW_MS = 5 * 60_000;

interface Props {
  messages: SupportMessage[];
  /** Whose side we render from: their messages sit on the right. */
  viewer: 'vendor' | 'staff';
  className?: string;
  emptyState?: React.ReactNode;
}

const authorKey = (m: SupportMessage) => `${m.authorKind}:${m.author?.email || m.author?.name || ''}:${m.internal ? 'n' : ''}`;

function dayLabel(date: string, t: (k: string) => string) {
  const d = dayjs(date);
  if (d.isSame(dayjs(), 'day')) return t('SUPPORT.TIMELINE.TODAY');
  if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return t('SUPPORT.TIMELINE.YESTERDAY');
  return d.format(d.isSame(dayjs(), 'year') ? 'dddd D MMMM' : 'D MMMM YYYY');
}

/** A task or status change, drawn as a line across the conversation. */
function SystemLine({ message, viewer }: { message: SupportMessage; viewer: Props['viewer'] }) {
  const { t } = useTranslation();
  const s = message.system!;
  const d = s.data || {};
  const type = d.type ? TASK_TYPE_META[d.type as TaskType] : null;
  const status = d.status && TASK_STATUS_META[d.status as TaskStatus];
  const TypeIcon = type?.icon;

  let content: React.ReactNode;
  if (s.type === 'status_changed') {
    const resolved = d.status === 'resolved';
    const Icon = resolved ? CircleCheck : RotateCcw;
    content = (
      <>
        <Icon className={cn('size-3.5', resolved ? 'text-success' : 'text-muted-foreground')} />
        {resolved
          ? t(viewer === 'vendor' ? 'SUPPORT.TIMELINE.RESOLVED_VENDOR' : 'SUPPORT.TIMELINE.RESOLVED_STAFF', { by: d.by })
          : t('SUPPORT.TIMELINE.REOPENED')}
      </>
    );
  } else {
    content = (
      <>
        {TypeIcon && <TypeIcon className={cn('size-3.5', type.tone)} />}
        <span>
          {s.type === 'task_linked'
            ? t('SUPPORT.TIMELINE.TASK_LINKED', { type: type ? t(type.labelKey) : '', number: d.number })
            : t('SUPPORT.TIMELINE.TASK_MOVED', { type: type ? t(type.labelKey) : '', number: d.number })}
        </span>
        {d.title && <span className="max-w-56 truncate font-medium text-foreground">“{d.title}”</span>}
        {status && (
          <span className="inline-flex items-center gap-1">
            {s.type === 'task_status' && <ArrowRight className="size-3" />}
            <span className={cn('size-1.5 rounded-full', status.dot)} />
            {t(status.labelKey)}
          </span>
        )}
      </>
    );
  }

  return (
    <div className="my-1 flex justify-center" data-testid="system-line">
      <div
        className={cn(
          'flex max-w-full flex-wrap items-center justify-center gap-1.5 rounded-full border bg-card px-3 py-1 text-xs text-muted-foreground',
          message.internal && 'border-dashed',
        )}
      >
        {content}
        <span className="text-muted-foreground/70">· {dayjs(message.createdAt).format('HH:mm')}</span>
      </div>
    </div>
  );
}

/**
 * The conversation: grouped bubbles, day separators, attachments, and system
 * lines for tasks and status changes. Sticks to the bottom as messages arrive,
 * unless the reader scrolled up.
 */
export function MessageTimeline({ messages, viewer, className, emptyState }: Props) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const lastCount = useRef(0);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const last = messages[messages.length - 1];
    const mine = last && ((viewer === 'vendor' && last.authorKind === 'vendor') || (viewer === 'staff' && last.authorKind === 'support'));
    if (messages.length !== lastCount.current && (stickToBottom.current || mine || lastCount.current === 0)) {
      el.scrollTop = el.scrollHeight;
    }
    lastCount.current = messages.length;
  }, [messages, viewer]);

  // Images load after layout: keep the bottom in view while they do.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onLoad = () => {
      if (stickToBottom.current) el.scrollTop = el.scrollHeight;
    };
    el.addEventListener('load', onLoad, true);
    return () => el.removeEventListener('load', onLoad, true);
  }, []);

  const mineKind = viewer === 'vendor' ? 'vendor' : 'support';

  return (
    <div
      ref={scrollRef}
      className={cn('flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-4 py-3', className)}
      onScroll={(e) => {
        const el = e.currentTarget;
        stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      }}
      data-testid="message-timeline"
    >
      {messages.length === 0 && emptyState}
      <div className="mt-auto flex flex-col gap-1">
        {messages.map((m, i) => {
          const prev = messages[i - 1];
          const newDay = !prev || !dayjs(prev.createdAt).isSame(m.createdAt, 'day');
          const separator = newDay && (
            <div className="my-3 flex items-center gap-3 text-[11px] font-medium text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              {dayLabel(m.createdAt, t)}
              <span className="h-px flex-1 bg-border" />
            </div>
          );

          if (m.authorKind === 'system') {
            return (
              <Fragment key={m._id}>
                {separator}
                <SystemLine message={m} viewer={viewer} />
              </Fragment>
            );
          }

          const mine = m.authorKind === mineKind;
          const startsGroup =
            newDay || !prev || prev.authorKind === 'system' || authorKey(prev) !== authorKey(m) || new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() > GROUP_WINDOW_MS;
          const next = messages[i + 1];
          const endsGroup =
            !next || next.authorKind === 'system' || authorKey(next) !== authorKey(m) || !dayjs(next.createdAt).isSame(m.createdAt, 'day') ||
            new Date(next.createdAt).getTime() - new Date(m.createdAt).getTime() > GROUP_WINDOW_MS;
          const who =
            m.authorKind === 'support'
              ? m.author?.name || t('SUPPORT.TIMELINE.ARDEALY')
              : m.author?.name || m.author?.email || t('SUPPORT.TIMELINE.VENDOR');

          return (
            <Fragment key={m._id}>
              {separator}
              <div className={cn('flex flex-col gap-1', startsGroup && 'mt-2', mine ? 'items-end' : 'items-start', m.pending && 'opacity-60')} data-testid="message">
                {startsGroup && (
                  <span className="flex items-center gap-1.5 px-1 text-[11px] font-medium text-muted-foreground">
                    {m.internal && <Lock className="size-3 text-warning" />}
                    {m.internal ? t('SUPPORT.TIMELINE.INTERNAL_NOTE', { who }) : who}
                  </span>
                )}
                {m.body && m.internal && (
                  // Not a Bubble: its variant colours win over any class set here.
                  <div className="max-w-[85%] rounded-xl border border-dashed border-warning/60 bg-warning/10 px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap wrap-break-word">
                    {m.body}
                  </div>
                )}
                {m.body && !m.internal && (
                  <Bubble variant={mine ? 'default' : 'muted'} align={mine ? 'end' : 'start'} className="max-w-[85%]">
                    <BubbleContent className="whitespace-pre-wrap">{m.body}</BubbleContent>
                  </Bubble>
                )}
                <AttachmentView attachments={m.attachments} align={mine ? 'end' : 'start'} />
                {endsGroup && (
                  <span className="px-1 text-[11px] text-muted-foreground/80">
                    {m.pending ? t('SUPPORT.TIMELINE.SENDING') : dayjs(m.createdAt).format('HH:mm')}
                  </span>
                )}
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
